import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../global/default-button/default-button';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { SalesforceOrderViewModel } from './salesforce-order.viewmodel';
import { ADD_ORDER_BUTTON_TEXT, NO_ORDER_TEXT, VIEW_ORDER_BUTTON_TEXT } from './salesforce-order.texts';
import './salesforce-order.scss';

interface IProps {
    vm: SalesforceOrderViewModel;
}

/**
 * Event-page control for the Salesforce Order, next to the event name (OEP-35059 view, OEP-35085 add).
 * Display matrix (AC #1):
 *  - Linked order + View permission          -> 'View Order' (opens the read-only modal)
 *  - No linked order + Create permission      -> '+ Add Order' (opens the Add form sub-window)
 *  - No linked order + View permission only   -> 'No Order' label (disabled)
 *  - No relevant Salesforce permission        -> nothing
 */
const SalesforceOrderControl = mobxReact.observer((props: IProps) => {
    const { vm } = props;

    if (!vm) {
        return null;
    }

    const canView = hasPermission(Permission.VIEW_SALESFORCE_ORDER);
    const canCreate = hasPermission(Permission.CREATE_ORDER);

    if (vm.hasLinkedOrder) {
        if (!canView) {
            return null;
        }
        return (
            <DefaultButton
                className='visible-while-hovered salesforce-order-control__view'
                modifier={['slim', 'link']}
                dataTestId='salesforce-order-view-button'
                text={<FormattedMessage {...VIEW_ORDER_BUTTON_TEXT} />}
                onClick={vm.open}
            />
        );
    }

    if (canCreate) {
        return (
            <DefaultButton
                className='visible-while-hovered salesforce-order-control__add'
                modifier={['slim', 'link']}
                dataTestId='salesforce-order-add-button'
                text={<FormattedMessage {...ADD_ORDER_BUTTON_TEXT} />}
                onClick={vm.openAddOrder}
            />
        );
    }

    if (canView) {
        return (
            <DefaultButton
                className='salesforce-order-control__no-order'
                modifier={['slim', 'link']}
                dataTestId='salesforce-order-no-order'
                text={<FormattedMessage {...NO_ORDER_TEXT} />}
                disabled={true}
            />
        );
    }

    return null;
});

export { SalesforceOrderControl };
